/**
 * Sprint 232 — CreatorPreviewDrawer
 *
 * Side-drawer preview opened when a client clicks an Influencer card.
 * Shows intro video, large portfolio gallery, full brand list, stats,
 * social handles, plus actions: Save / Message / Book / View Full Profile.
 */
import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import {
  CheckCircle2, Play, MapPin, Heart, Instagram, Users, TrendingUp,
  Eye, ExternalLink, Mail, FileText,
} from 'lucide-react';

const TIER_LABEL = {
  ugc: 'UGC Creator',
  micro: 'Micro Influencer',
  mid: 'Mid-Tier Influencer',
  macro: 'Macro Influencer',
  mega: 'Mega Influencer',
  nano: 'Nano Influencer',
};

const formatCount = (n) => {
  if (!n) return '0';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

// Convert YouTube/Vimeo URL → embeddable iframe src
const toEmbed = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')) {
      const id = u.hostname.includes('youtu.be')
        ? u.pathname.replace('/', '')
        : u.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch { /* ignore */ }
  return null;
};

const CreatorPreviewDrawer = ({ influencer, open, onClose, onToggleFav, isFav, onViewProfile, onBook }) => {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  if (!influencer) return null;

  const i = influencer;
  const tierLabel = TIER_LABEL[i.influencer_tier] || i.influencer_tier;
  const followers = i.follower_count || i.total_reach || 0;
  const uploadVideo = i.intro_video_upload_url;
  const embedUrl = !uploadVideo ? toEmbed(i.intro_video_url) : null;
  const portfolio = i.portfolio_media || [];

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 bg-[#0B0F14] border-l border-white/10 text-white overflow-y-auto"
        data-testid="creator-preview-drawer"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>{i.name}</SheetTitle>
        </SheetHeader>

        {/* Hero */}
        <div className="relative">
          {/* Intro video (upload first, then embed, then static photo) */}
          {uploadVideo ? (
            <video
              src={uploadVideo}
              controls
              playsInline
              className="w-full aspect-video bg-black"
              data-testid="intro-video-player"
            />
          ) : embedUrl ? (
            <div className="w-full aspect-video bg-black">
              <iframe
                src={embedUrl}
                title="Intro video"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          ) : i.photo ? (
            // Iter-234b — was `aspect-video object-cover` which severely
            // cropped portrait creator photos on mobile (face cut off).
            // Iter-313h — user reports the popup profile picture is still
            // being cut in half. Switch to `object-contain` so the full
            // photo is always visible (letterboxed on the sides if the
            // aspect doesn't match) instead of cropped.
            <img
              src={i.photo}
              alt={i.name}
              className="w-full aspect-[4/5] sm:aspect-video object-contain object-center bg-[#0B0F14]"
              loading="lazy"
              data-testid="drawer-hero-photo"
            />
          ) : (
            <div className="w-full aspect-[4/5] sm:aspect-video bg-gradient-to-br from-[#101820] to-[#0B0F14] grid place-items-center">
              <Users className="w-12 h-12 text-white/30" />
            </div>
          )}

          <div className="absolute inset-x-0 -bottom-0.5 h-16 bg-gradient-to-t from-[#0B0F14] to-transparent pointer-events-none" />
        </div>

        {/* Identity */}
        <div className="px-5 pt-4 pb-2">
          <div className="flex items-start gap-3">
            {i.photo && (
              <img
                src={i.photo}
                alt={i.name}
                className="w-16 h-16 rounded-full object-cover border-2 border-[#0DEAE3]/40 -mt-12 shadow-xl"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold truncate" data-testid="drawer-name">{i.name}</h2>
                {i.is_verified && <CheckCircle2 className="w-4 h-4 text-[#0DEAE3] flex-shrink-0" />}
              </div>
              {(i.city || i.state) && (
                <p className="text-sm text-white/60 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {[i.city, i.state].filter(Boolean).join(', ')}
                </p>
              )}
              {tierLabel && (
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#0DEAE3]/15 border border-[#0DEAE3]/30 text-[#0DEAE3]">
                  {tierLabel}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={onToggleFav}
              className={`w-10 h-10 rounded-full grid place-items-center border ${
                isFav ? 'bg-[#FF2D95] border-[#FF2D95] text-white' : 'bg-white/5 border-white/10 text-white/70 hover:text-white'
              }`}
              data-testid="drawer-fav-btn"
              aria-label={isFav ? 'Unfavorite' : 'Favorite'}
            >
              <Heart className={`w-4 h-4 ${isFav ? 'fill-white' : ''}`} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="px-5 grid grid-cols-3 gap-2 mt-3">
          <KPI icon={Users} label="Followers" value={formatCount(followers)} />
          <KPI icon={TrendingUp} label="Engagement" value={i.engagement_rate != null ? `${Number(i.engagement_rate).toFixed(1)}%` : '—'} />
          <KPI icon={Eye} label="Avg Views" value={formatCount(i.average_views)} />
        </div>

        {/* Categories */}
        {(i.creator_categories || []).length > 0 && (
          <Section title="Creator Categories">
            <div className="flex flex-wrap gap-1.5">
              {i.creator_categories.map(c => (
                <span key={c} className="px-2.5 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-white/80">
                  {c}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* Bio */}
        {i.bio && (
          <Section title="About">
            <p className="text-sm text-white/70 leading-relaxed line-clamp-6">{i.bio}</p>
          </Section>
        )}

        {/* Worked With */}
        {(i.worked_with_brands || []).length > 0 && (
          <Section title="Worked With">
            <div className="flex flex-wrap gap-1.5" data-testid="drawer-worked-with">
              {i.worked_with_brands.map((b) => (
                <span
                  key={b.brand_name}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-[#0DEAE3]/8 border border-[#0DEAE3]/25 text-[#A5F8F4]"
                  title={b.source === 'auto' ? 'Auto-derived from completed bookings' : 'Manually added by creator'}
                >
                  {b.brand_logo_url && (
                    <img src={b.brand_logo_url} alt="" className="w-3 h-3 rounded-sm object-cover" />
                  )}
                  {b.brand_name}
                  {b.source === 'auto' && (
                    <CheckCircle2 className="w-2.5 h-2.5 opacity-60" />
                  )}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* Portfolio */}
        {portfolio.length > 0 && (
          <Section title="Portfolio">
            <div className="grid grid-cols-3 gap-1.5" data-testid="drawer-portfolio">
              {portfolio.slice(0, 9).map((url, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setLightboxIdx(idx)}
                  className="aspect-square overflow-hidden rounded-lg border border-white/5 hover:border-[#0DEAE3]/40 transition-colors"
                >
                  <img src={url} alt={`Portfolio ${idx + 1}`} loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* Socials */}
        <Section title="Social Reach">
          <div className="space-y-1.5">
            {Object.entries(i.social_stats || {}).map(([platform, s]) => {
              const count = s.followers || s.subscribers || s.connections || 0;
              if (!count) return null;
              const handle = s.handle || s.channel || s.url;
              return (
                <div key={platform} className="flex items-center justify-between text-sm bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2">
                  <span className="capitalize text-white/80 flex items-center gap-2">
                    {platform === 'instagram' && <Instagram className="w-3.5 h-3.5 text-pink-400" />}
                    {platform}
                    {handle && <span className="text-white/40 text-[11px]">{handle}</span>}
                  </span>
                  <span className="font-semibold text-[#0DEAE3]">{formatCount(count)}</span>
                </div>
              );
            })}
          </div>
        </Section>

        {/* Media Kit */}
        {i.media_kit_url && (
          <div className="px-5 pb-2">
            <a
              href={i.media_kit_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#0DEAE3] hover:underline"
            >
              <FileText className="w-4 h-4" /> View Media Kit
            </a>
          </div>
        )}

        {/* Sticky Action Bar */}
        <div className="sticky bottom-0 inset-x-0 bg-[#0B0F14]/95 backdrop-blur-md border-t border-white/10 px-5 py-3 flex items-center gap-2 mt-4">
          <Button
            onClick={onViewProfile}
            className="flex-1 bg-[#0DEAE3] hover:bg-[#0DEAE3]/90 text-[#0B0F14] font-semibold"
            data-testid="view-profile-btn"
          >
            View Full Profile <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </Button>
          <Button
            onClick={onBook}
            variant="outline"
            className="border-white/15 bg-white/5 text-white hover:bg-white/10"
            data-testid="book-creator-btn"
          >
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Book
          </Button>
        </div>

        {/* Lightbox */}
        {lightboxIdx !== null && portfolio[lightboxIdx] && (
          <button
            type="button"
            onClick={() => setLightboxIdx(null)}
            className="fixed inset-0 z-[60] bg-black/90 grid place-items-center"
          >
            <img src={portfolio[lightboxIdx]} alt="" className="max-w-[92vw] max-h-[92vh] object-contain" />
          </button>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Section = ({ title, children }) => (
  <div className="px-5 py-3 border-t border-white/5">
    <h4 className="text-[11px] uppercase tracking-wider text-white/40 mb-2">{title}</h4>
    {children}
  </div>
);

const KPI = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-3 text-center">
    <Icon className="w-3.5 h-3.5 text-[#0DEAE3] mx-auto mb-1" />
    <p className="text-lg font-bold text-white leading-none">{value}</p>
    <p className="text-[10px] uppercase tracking-wider text-white/40 mt-1">{label}</p>
  </div>
);

export default CreatorPreviewDrawer;
